import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef
} from '@angular/core';
import { TableColumn, TableSortEvent, TableRowClickEvent } from './table-column.interface';
import { AppTableTemplateDirective } from './app-table-template.directive';

@Component({
  selector: 'app-table',
  templateUrl: './app-table.component.html',
  styleUrls: ['./app-table.component.scss']
})
export class AppTableComponent {
  /** Array of column configurations */
  @Input() columns: TableColumn[] = [];

  /** Data array to render in table */
  @Input() data: any[] = [];

  /** Whether to show the sticky footer summary row */
  @Input() showFooter: boolean = false;

  /** Footer data object for summary totals */
  @Input() footerData: any = null;

  /** Custom footer template if provided */
  @Input() footerTemplate: TemplateRef<any> | null = null;

  /** Default label for total row first column */
  @Input() footerLabel: string = 'Total';

  /** Dictionary of custom cell templates by column key */
  @Input() customTemplates: { [key: string]: TemplateRef<any> } = {};

  /** Current active sort key */
  @Input() sortActive: string = '';

  /** Current sort direction ('asc' | 'desc' | '') */
  @Input() sortDirection: 'asc' | 'desc' | '' = '';

  /** Whether table rows are clickable with hover effect */
  @Input() rowClickable: boolean = true;

  /** CSS class or function returning CSS class for <tr> */
  @Input() rowClass: string | ((row: any, index: number) => string) = '';

  /** Whether table is in loading state */
  @Input() isLoading: boolean = false;

  /** Whether to show the standard empty state row if data is empty */
  @Input() showEmptyState: boolean = true;

  /** Main message for empty state */
  @Input() emptyMessage: string = 'No Data Found';

  /** Sub-message for empty state */
  @Input() emptySubMessage: string = 'No records match the current filter criteria.';

  /** Custom empty state template if provided */
  @Input() emptyTemplate: TemplateRef<any> | null = null;

  /** CSS class for outermost table section container */
  @Input() tableSectionClass: string = 'summary-table-section';

  /** CSS class for table scroll wrapper */
  @Input() tableWrapperClass: string = 'summary-table-wrapper';

  /** Optional custom max-height style for wrapper (e.g. '350px') */
  @Input() maxHeight: string = '';

  /** CSS class for <table> element */
  @Input() tableClass: string = 'summary-table';

  /** Emitted when a sortable column header is clicked */
  @Output() sortChange = new EventEmitter<TableSortEvent>();

  /** Emitted when a table row is clicked */
  @Output() rowClick = new EventEmitter<TableRowClickEvent>();

  @ContentChildren(AppTableTemplateDirective)
  private templateDirectives: QueryList<AppTableTemplateDirective>;

  /**
   * Retrieves the custom template for a column, checking both ContentChildren directives and Input dictionary.
   */
  getTemplate(key: string): TemplateRef<any> | null {
    if (this.customTemplates && this.customTemplates[key]) {
      return this.customTemplates[key];
    }
    if (this.templateDirectives) {
      const directive = this.templateDirectives.find((d) => d.name === key);
      if (directive) {
        return directive.template;
      }
    }
    return null;
  }

  /**
   * Resolves the display value for a cell.
   */
  getCellValue(row: any, col: TableColumn, index: number): any {
    if (!row) return '';
    if (col.formatter) {
      return col.formatter(row, row[col.key], index);
    }
    return row[col.key];
  }

  /**
   * Resolves the cell CSS classes.
   */
  getCellClass(row: any, col: TableColumn, index: number): string {
    const classes: string[] = [];

    if (col.sticky) {
      classes.push('col-sticky');
    }

    if (col.align) {
      if (col.align === 'start' || col.align === 'left') classes.push('text-start');
      else if (col.align === 'center') classes.push('text-center');
      else if (col.align === 'end' || col.align === 'right') classes.push('text-end');
    }

    if (col.cellClass) {
      if (typeof col.cellClass === 'function') {
        const dynamicClass = col.cellClass(row, row[col.key], index);
        if (dynamicClass) classes.push(dynamicClass);
      } else {
        classes.push(col.cellClass);
      }
    } else {
      classes.push('text-black');
    }

    return classes.join(' ');
  }

  /**
   * Resolves the header <th> CSS classes.
   */
  getHeaderClass(col: TableColumn): string {
    const classes: string[] = [];

    if (col.sticky) {
      classes.push('col-sticky');
    }

    if (col.sortable) {
      classes.push('cursor-pointer');
    }

    if (col.align) {
      if (col.align === 'start' || col.align === 'left') classes.push('text-start');
      else if (col.align === 'center') classes.push('text-center');
      else if (col.align === 'end' || col.align === 'right') classes.push('text-end');
    }

    if (col.headerClass) {
      classes.push(col.headerClass);
    }

    return classes.join(' ');
  }

  /**
   * Resolves the badge CSS class for type='badge'.
   */
  getBadgeClass(row: any, col: TableColumn, index: number): string {
    const value = this.getCellValue(row, col, index);
    if (col.badgeClass) {
      if (typeof col.badgeClass === 'function') {
        return col.badgeClass(row, value);
      }
      return col.badgeClass;
    }
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    return '';
  }

  /**
   * Resolves row CSS classes.
   */
  getRowClass(row: any, index: number): string {
    const classes: string[] = [];

    if (this.rowClickable) {
      classes.push('cursor-pointer');
    }

    if (this.rowClass) {
      if (typeof this.rowClass === 'function') {
        const dynamicClass = this.rowClass(row, index);
        if (dynamicClass) classes.push(dynamicClass);
      } else {
        classes.push(this.rowClass);
      }
    }

    return classes.join(' ');
  }

  /**
   * Resolves footer value for a column.
   */
  getFooterValue(col: TableColumn, colIndex: number): any {
    if (!this.footerData) {
      return colIndex === 0 ? this.footerLabel : '';
    }

    if (col.footerFormatter) {
      return col.footerFormatter(this.footerData, col);
    }

    const key = col.footerKey || col.key;
    if (this.footerData[key] !== undefined && this.footerData[key] !== null) {
      return this.footerData[key];
    }

    if (colIndex === 0 && (!col.footerKey || !this.footerData[col.footerKey])) {
      return this.footerLabel;
    }

    return '';
  }

  /**
   * Resolves footer <td> CSS classes.
   */
  getFooterClass(col: TableColumn, colIndex: number): string {
    const classes: string[] = [];

    if (col.sticky) {
      classes.push('col-sticky');
    }

    const align = col.footerAlign || col.align || 'start';
    if (align === 'start' || align === 'left') classes.push('text-start');
    else if (align === 'center') classes.push('text-center');
    else if (align === 'end' || align === 'right') classes.push('text-end');

    if (col.footerClass) {
      classes.push(col.footerClass);
    } else {
      classes.push('text-black font-semibold');
    }

    return classes.join(' ');
  }

  /**
   * Handles column header sort click.
   */
  onSort(col: TableColumn): void {
    if (!col.sortable) return;

    const sortKey = col.sortKey || col.key;
    let newDirection: 'asc' | 'desc' | '' = 'asc';

    if (this.sortActive === sortKey) {
      if (this.sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        newDirection = '';
      } else {
        newDirection = 'asc';
      }
    }

    this.sortActive = newDirection === '' ? '' : sortKey;
    this.sortDirection = newDirection;

    this.sortChange.emit({
      active: this.sortActive,
      direction: this.sortDirection
    });
  }

  /**
   * Handles row click event.
   */
  onRowClick(row: any, index: number, event: MouseEvent): void {
    if (this.rowClickable) {
      this.rowClick.emit({ row, index, event });
    }
  }

  /**
   * Stops propagation on nested clickable elements.
   */
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
